/**
 * Currency helpers — frontend mirror of backend
 * `backend/src/services/billing/billingCalculations.js`.
 *
 * Rounding model:
 * - Round to 2 decimals using `Number.EPSILON` to dodge FP edge cases.
 * - When `unit_price_tvac` is known (tarif-imported price), TVAC is the
 *   source of truth: `lineTvac = round(qty × unit_price_tvac)`, HTVA derived,
 *   TVA = TVAC − HTVA (exact).
 * - Otherwise (free-text custom lines) HTVA is the source and TVA is
 *   computed as `round(HTVA × rate)`.
 *
 * Keep this file in lock-step with billingCalculations.js — drift means the
 * preview shows a different number than what gets persisted.
 */

const DEFAULT_TVA_RATE = 21;

export function roundCurrency(value: number | null | undefined): number {
  if (value === null || value === undefined || Number.isNaN(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

// Use Number.isFinite to keep a legitimate 0 (exempt VAT) — `|| DEFAULT` would
// coerce 0 to 21 and produce wrong line totals on 0 % VAT lines.
function normaliseRate(tvaRate: number): number {
  const r = Number(tvaRate);
  return Number.isFinite(r) ? r : DEFAULT_TVA_RATE;
}

export function calculateLineTotals(
  quantity: number,
  unitPriceHtva: number,
  tvaRate: number = DEFAULT_TVA_RATE
): { lineHtva: number; lineTva: number; lineTvac: number } {
  const qty = Number(quantity) || 0;
  const unitPrice = Number(unitPriceHtva) || 0;
  const rate = normaliseRate(tvaRate);

  const lineHtva = roundCurrency(qty * unitPrice);
  const lineTva = roundCurrency(lineHtva * (rate / 100));
  const lineTvac = roundCurrency(lineHtva + lineTva);

  return { lineHtva, lineTva, lineTvac };
}

export function calculateLineTotalsFromTvac(
  quantity: number,
  unitPriceTvac: number,
  tvaRate: number = DEFAULT_TVA_RATE
): { lineHtva: number; lineTva: number; lineTvac: number; unitPriceHtva: number } {
  const qty = Number(quantity) || 0;
  const unitTvac = Number(unitPriceTvac) || 0;
  const rate = normaliseRate(tvaRate);

  const unitPriceHtva = roundCurrency(unitTvac / (1 + rate / 100));
  const lineTvac = roundCurrency(qty * unitTvac);
  const lineHtva = roundCurrency(qty * unitPriceHtva);
  // Exact TVA via difference — avoids HTVA × rate rounding drift.
  const lineTva = roundCurrency(lineTvac - lineHtva);

  return { lineHtva, lineTva, lineTvac, unitPriceHtva };
}
